import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize from 'rehype-sanitize'
import rehypeExternalLinks from 'rehype-external-links'
import {
  preprocessMarkdown,
  shortenLinkText,
  fixMalformedHref,
} from '../utils/markdownUtils'

/**
 * Renders streamed/assistant markdown with the project's shared config:
 * GitHub-flavored markdown, sanitized HTML, external links forced to a new tab,
 * and the custom link renderer that repairs malformed hrefs and shortens long
 * link text. Memoized so re-renders with the same `content` don't re-parse —
 * which matters during streaming where the parent updates every frame.
 *
 * Previously this exact config was copy-pasted across AssistantBubble (CoSell
 * chat), ChatWindow and InterleavedStageContent (Propensity chat); this is the
 * single source of truth.
 */
export const AssistantMarkdown = React.memo<{ content: string }>(
  ({ content }) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[
        rehypeSanitize,
        [
          rehypeExternalLinks,
          { target: '_blank', rel: ['noopener', 'noreferrer'] },
        ],
      ]}
      components={{
        a: ({ href, children, node, ...props }) => {
          const url = fixMalformedHref(href || '')
          const childArray = React.Children.toArray(children)
          const first = childArray[0]
          const display =
            typeof first === 'string' ? shortenLinkText(url, first) : childArray
          return (
            <a href={url} target="_blank" rel="noopener noreferrer" {...props}>
              {display}
            </a>
          )
        },
      }}
    >
      {preprocessMarkdown(content)}
    </ReactMarkdown>
  )
)
AssistantMarkdown.displayName = 'AssistantMarkdown'
